import type { Routes } from '@angular/router';
import { staffAuthGuard } from './core/staff-auth.guard';

export const appRoutes: Routes = [
  // 미인증 랜딩 — guard 없음(guard 있으면 재진입 루프 발생). 헤더는 항상 보인다(app.ts).
  {
    path: 'welcome',
    loadComponent: () =>
      import('./features/welcome/welcome.component').then(
        (m) => m.WelcomeComponent,
      ),
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/login/login.component').then((m) => m.LoginComponent),
  },
  // 초대 미수락(Pending) staff 가 로그인 후 도착하는 블로킹 화면 — guard 없음(자체적으로
  // 세션 유효성을 확인하고 실패 시 /login 으로 이동한다). staffAuthGuard 가 여기로 리다이렉트.
  // hideLogout: true — 헤더 자체는 다른 라우트와 동일하게 보이지만, 로그아웃(탈출 경로)만
  // 감춰 이 화면의 "탈출 불가" 성질을 지킨다(app.ts).
  {
    path: 'invitation',
    loadComponent: () =>
      import('./features/invitation/invitation.component').then(
        (m) => m.InvitationComponent,
      ),
    data: { hideLogout: true },
  },
  // 초대 수락 직후 부여된 권한이 하나도 없는 Member가 도착하는 안내 화면(B1, accounts-staff-web
  // 이식). staffAuthGuard를 적용해 미인증/Pending/Rejected가 URL 직접 입력으로 들어오는 것을
  // 막고(2026-08-11 Gemini 리뷰로 발견), preventIfHasScopes로 이미 권한이 있는 staff가 북마크·
  // 뒤로가기로 들어오면 홈(`/`)으로 되돌린다(requireScopes의 반대 방향 가드).
  {
    path: 'invitation-accepted',
    canActivate: [staffAuthGuard],
    loadComponent: () =>
      import(
        './features/invitation-accepted/invitation-accepted.component'
      ).then((m) => m.InvitationAcceptedComponent),
    data: { preventIfHasScopes: true },
  },
  // requiresStaff OIDC client(commerce-staff/sms-staff/grafana 등)의 interaction 목적지
  // (oidc.module.ts interactions.url, 2026-08-11 Phase A2) — completeOidcInteraction()이
  // 실제 API 왕복을 하는 동안(및 실패 시 에러 메시지 표시 중) 화면이 잠깐 이상 떠 있을 수
  // 있어, 헤더는 다른 라우트와 동일하게 항상 보인다(2026-08-12, "헤더는 모든 라우트에서
  // 항상 보이거나 항상 안 보이거나 둘 중 하나" 결정 — 예전엔 shellMode:'none'으로 쉘
  // 크롬을 껐었지만 지금은 라우트별 예외를 두지 않는다). staffAuthGuard를 그대로 재사용해
  // 미인증/초대 미수락 분기를 중복 구현하지 않는다.
  {
    path: 'oauth/authorize',
    canActivate: [staffAuthGuard],
    loadComponent: () =>
      import('./features/oauth-authorize/oauth-authorize.component').then(
        (m) => m.OauthAuthorizeComponent,
      ),
  },
  {
    path: '',
    canActivate: [staffAuthGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./features/staff-home/staff-home.component').then(
            (m) => m.StaffHomeComponent,
          ),
      },
      // requireScopes: true — hasEffectiveScopes===false인 Accepted staff를 /staff 진입마다
      // /invitation-accepted로 되돌린다(staff-auth.guard.ts 참고). `/staff/*`(staff 관리)는
      // 백엔드가 `StaffManagementGuard`(Owner 또는 staff.editor scope)로 실제 게이트하므로
      // 프론트도 같은 기준으로 막는다. `/`(홈)·`/oauth-clients`·`/org-webhooks`에는 이 플래그를
      // 걸지 않는다 — 백엔드가 `StaffAcceptedGuard`(Accepted 상태만 확인, scope 무관)로 이미
      // 모든 Accepted staff에게 열어두고 있어(oauth-client-review 심사는 scope 없는 staff도
      // 참여 가능하도록 의도된 설계), 프론트에서 이 라우트들까지 막으면 백엔드가 허용하는
      // 정당한 접근을 프론트가 임의로 차단하는 셈이 된다 — cross-app e2e(SC5~7)의 심사자
      // 세션(ACCEPTED_STAFF_ACCOUNT, scope 없음)이 /oauth-clients에 못 들어가 타임아웃나는
      // 회귀로 실제 발견(2026-08-11).
      {
        path: 'staff',
        canActivate: [staffAuthGuard],
        data: { requireScopes: true },
        loadComponent: () =>
          import('./features/staff/staff-list/staff-list.component').then(
            (m) => m.StaffListComponent,
          ),
      },
      {
        path: 'staff/:id',
        canActivate: [staffAuthGuard],
        data: { requireScopes: true },
        loadComponent: () =>
          import('./features/staff/staff-detail/staff-detail.component').then(
            (m) => m.StaffDetailComponent,
          ),
      },
      {
        path: 'oauth-clients',
        loadComponent: () =>
          import(
            './features/oauth-client-review/oauth-client-review-list.component'
          ).then((m) => m.OAuthClientReviewListComponent),
      },
      {
        path: 'oauth-clients/:id',
        loadComponent: () =>
          import(
            './features/oauth-client-review/oauth-client-review-detail.component'
          ).then((m) => m.OAuthClientReviewDetailComponent),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/users/user-search.component').then(
            (m) => m.UserSearchComponent,
          ),
      },
      {
        path: 'users/:userId',
        loadComponent: () =>
          import('./features/users/user-detail.component').then(
            (m) => m.UserDetailComponent,
          ),
      },
      {
        path: 'org-webhooks',
        loadComponent: () =>
          import('@sbe-spinoff-client/webhook-events-admin').then(
            (m) => m.WebhookEventListComponent,
          ),
        data: { detailRoute: 'org-webhooks' },
      },
      {
        path: 'org-webhooks/:id',
        loadComponent: () =>
          import('@sbe-spinoff-client/webhook-events-admin').then(
            (m) => m.WebhookEventDetailComponent,
          ),
        data: { listRoute: 'org-webhooks' },
      },
    ],
  },
];
